import { useEffect, useState } from 'react'

import './_paymentMethod.scss'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import { Button } from 'components/Button/Button'
import InputCard from 'components/Form/InputCard/InputCard'
import { addToast } from 'components/Toast/Toast'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import { useModalContext } from 'context/modalContext'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import { Icons } from 'helpers/icons'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import {
    sendSecurityVerificationCode,
    verifySecurityVerificationCode
} from 'services/endpoints/settings/account/profileSettings'
import { putBasicPayoutSettings } from 'services/endpoints/settings/general/payout'
import {
    useSetUserProfile,
    useUserProfile
} from 'store'

type PayoutMethod = 'wire' | 'ach' | 'paxum'

interface ConfirmSecurityCodeProps {
    loading: boolean
    resendLoading: boolean
    onConfirm: (code: string) => void
    onResend: () => void
}

const ConfirmSecurityCode = ({
    loading,
    resendLoading,
    onConfirm,
    onResend
}: ConfirmSecurityCodeProps) => {
    const [code, setCode] = useState('')
    const { t } = useTranslation()

    return (
        <div className='security-code-modal'>
            <InputCard
                type='text'
                label={t('verificationCode')}
                value={code}
                changeFn={(value: string) => {
                    setCode(
                        value
                            .replace(/\D/g, '')
                            .slice(0, 6)
                    )
                }}
            />

            <div className='security-code-modal__sent'>
                {t('A verification code was sent to your email.')}
            </div>

            <div className='security-code-modal__actions'>
                <Button
                    text={t('resendCode')}
                    color='grey'
                    font='mont-14-normal'
                    height='3'
                    width='10'
                    disabled={resendLoading}
                    clickFn={onResend}
                />

                <Button
                    text={t('verify')}
                    color='black'
                    font='mont-14-normal'
                    height='4'
                    width='14'
                    disabled={code.length !== 6 || loading}
                    clickFn={() => {
                        if (code.length !== 6) return

                        onConfirm(code)
                    }}
                />
            </div>
        </div>
    )
}

export const PaymentMethod = () => {
    const { t } = useTranslation()
    const profile = useUserProfile()!
    const modal = useModalContext()
    const queryClient = useQueryClient()

    const [paymentMethodSelected, setPaymentMethodSelected] = useState<
        string | null
    >(profile.payout_method ?? null)

    const [pendingMethod, setPendingMethod] =
        useState<PayoutMethod | null>(null)

    const [verificationId, setVerificationId] =
        useState<string | null>(null)

    useEffect(() => {
        setPaymentMethodSelected(profile.payout_method ?? null)
    }, [profile.payout_method])

    const getStatus = (method: string) => {
        return profile.payout_method === method
            ? t('Active')
            : t('Inactive')
    }

    const getSubtext = (
        method: string,
        paxumEmail?: string | null
    ) => {
        const isActive =
            profile.payout_method === method

        return (
            <div className='payment-method__details'>
                <span
                    className={`paymentMethod__status ${
                        isActive
                            ? 'paymentMethod__status--active'
                            : 'paymentMethod__status--inactive'
                    }`}
                >
                    {getStatus(method)}
                </span>

                {method === 'paxum' && paxumEmail && (
                    <span className='payment-method__email'>
                        {paxumEmail}
                    </span>
                )}
            </div>
        )
    }

const updatePayoutMethodMutation = useMutation(
    async (method: PayoutMethod) => {
        const payload: {
            payout_method: PayoutMethod
            verification_id?: string
        } = {
            payout_method: method
        }

        if (verificationId) {
            payload.verification_id = verificationId
        }

        return await putBasicPayoutSettings(payload)
    },
    {
        onSuccess: async (_, method) => {
            modal.clearModal()

            setPaymentMethodSelected(method)

            useSetUserProfile({
                payout_method: method
            })

            setPendingMethod(null)
            setVerificationId(null)

            await queryClient.invalidateQueries(
                'user-profile'
            )
        },

        onError: (error: any) => {
            const errors =
                error?.response?.data?.errors ??
                error?.response?.data?.error?.errors ??
                error?.response?.data?.message

            if (errors?.security_verification?.length) {
                addToast(
                    'error',
                    String(errors.security_verification[0])
                )
                return
            }

            if (errors?.verification_id?.length) {
                addToast(
                    'error',
                    String(errors.verification_id[0])
                )
                return
            }

            addToast(
                'error',
                t('Unable to update payout method.')
            )
        }
    }
)
    const verifyCodeMutation = useMutation(
        async (code: string) => {
            if (!verificationId) {
                throw new Error(
                    'Missing security verification ID.'
                )
            }

            return await verifySecurityVerificationCode(
                verificationId,
                code
            )
        },
        {
            onSuccess: () => {
                if (!pendingMethod) return

                updatePayoutMethodMutation.mutate(
                    pendingMethod
                )
            },

            onError: (error: any) => {
                const errors =
                    error?.response?.data?.errors ??
                    error?.response?.data?.error?.errors

                if (errors?.code?.length) {
                    addToast(
                        'error',
                        String(errors.code[0])
                    )

                    return
                }

                if (
                    errors?.verification_id?.length
                ) {
                    addToast(
                        'error',
                        String(
                            errors.verification_id[0]
                        )
                    )

                    return
                }

                addToast(
                    'error',
                    t('Invalid verification code.')
                )
            }
        }
    )

    const openVerificationModal = () => {
        modal.clearModal()

        modal.addModal(
            t('Verify Email'),
            <ConfirmSecurityCode
                loading={verifyCodeMutation.isLoading}
                resendLoading={sendCodeMutation.isLoading}
                onConfirm={(code: string) => {
                    verifyCodeMutation.mutate(code)
                }}
                onResend={() => {
                    sendCodeMutation.mutate()
                }}
            />
        )
    }

    const sendCodeMutation = useMutation(
        async () => {
            if (!pendingMethod) {
                throw new Error(
                    'Missing pending payout method.'
                )
            }

            return await sendSecurityVerificationCode(
                'change_payout_method',
                {
                    payout_method: pendingMethod
                }
            )
        },
        {
            onSuccess: response => {
                const id =
                    response?.data?.verification_id

                if (!id) {
                    addToast(
                        'error',
                        'Unable to create security verification.'
                    )

                    return
                }

                setVerificationId(id)

                openVerificationModal()
            },

            onError: () => {
                addToast(
                    'error',
                    t('Unable to send verification code.')
                )
            }
        }
    )

    const requestPayoutMethodChange = (
        method: PayoutMethod
    ) => {
        if (profile.payout_method === method) {
            return
        }

        setPendingMethod(method)
        setVerificationId(null)

        /*
        * First-time payout method selection.
        * No security verification required.
        */
        if (!profile.payout_method) {
            updatePayoutMethodMutation.mutate(method)
            return
        }

        /*
        * Existing payout method is being changed.
        * Security verification is required.
        */
        sendSecurityVerificationCode(
            'change_payout_method',
            {
                payout_method: method
            }
        )
            .then(response => {
                const id =
                    response?.data?.verification_id

                if (!id) {
                    addToast(
                        'error',
                        'Unable to create security verification.'
                    )
                    return
                }

                setVerificationId(id)

                modal.clearModal()

                modal.addModal(
                    t('Verify Email'),
                    <ConfirmSecurityCode
                        loading={verifyCodeMutation.isLoading}
                        resendLoading={sendCodeMutation.isLoading}
                        onConfirm={(code: string) => {
                            verifyCodeMutation.mutate(code)
                        }}
                        onResend={() => {
                            sendCodeMutation.mutate()
                        }}
                    />
                )
            })
            .catch(() => {
                addToast(
                    'error',
                    t('Unable to send verification code.')
                )
            })
    }
    const paymentOptions = [
        {
            method: 'wire' as const,
            link: '/settings/general/payout-settings/payment-method/wire',
            icon: Icons.payment_bank,
            text: t('wire'),
            iconSize: 'big' as const,
            hasRadio: true,
            inputId: 'payment_wire',
            toggleActive:
                paymentMethodSelected === 'wire',
            toggleFn: () => {
                requestPayoutMethodChange('wire')
            },
            clickFn: undefined,
            hasArrow: true,
            subtext: getSubtext('wire')
        },
        {
            method: 'ach' as const,
            link: '/settings/general/payout-settings/payment-method/ach',
            icon: Icons.payment_bank,
            text: t('ach'),
            iconSize: 'big' as const,
            hasRadio: true,
            inputId: 'payment_ach',
            toggleActive:
                paymentMethodSelected === 'ach',
            toggleFn: () => {
                requestPayoutMethodChange('ach')
            },
            clickFn: undefined,
            hasArrow: true,
            subtext: getSubtext('ach')
        },
        {
            method: 'paxum' as const,
            link: '/settings/general/payout-settings/payment-method/paxum',
            icon: Icons.paxum,
            iconSize: 'big' as const,
            text: t('Paxum'),
            hasRadio: true,
            inputId: 'paxum',
            toggleActive:
                paymentMethodSelected === 'paxum',
            toggleFn: () => {
                requestPayoutMethodChange('paxum')
            },
            clickFn: undefined,
            hasArrow: true,
            subtext: getSubtext(
                'paxum',
                profile.paxum_email
            )
        }
    ]

    return (
        <WithHeaderSection
            headerSection={
                <LayoutHeader
                    type='basic'
                    section={t('general')}
                    title={t('payoutMethod')}
                />
            }
        >
            {paymentOptions
                .filter(opt => {
                    if (
                        profile.country !==
                        'United States'
                    ) {
                        return (
                            opt.link !==
                            '/settings/general/payout-settings/payment-method/ach'
                        )
                    }

                    return true
                })
                .map(opt => (
                    <ActionCard
                        key={opt.method}
                        link={opt.link}
                        icon={opt.icon}
                        iconSize={opt.iconSize}
                        selected={opt.toggleActive}
                        text={opt.text}
                        subtext={opt.subtext}
                        hasRadio={opt.hasRadio}
                        inputId={opt.inputId}
                        toggleActive={opt.toggleActive}
                        toggleFn={opt.toggleFn}
                        hasArrow={opt.hasArrow}
                        clickFn={opt.clickFn}
                    />
                ))}
        </WithHeaderSection>
    )
}